<template>
  <div class="list">
    <div v-if="title" class="list__header" :class="{ 'list__header-w-icon': icon }">
      <div class="list__header-title">
        <component :is="icon"></component>
        {{ title }}
      </div>
      <div v-if="title" class="list__header-amount">({{ isLoading ? '-' : items.length }})</div>
    </div>
    <ul v-if="items.length && !isLoading" class="list__items">
      <ListRow
        v-for="item in items"
        :key="item.id"
        :header="item.header"
        :subheader="item.subheader"
        :icon="item.icon"
        :imgSource="item.imgSource"
        :hasTooltip="hasTooltips"
        tabindex="0"
        @click="onSelect(item)"
        @keydown.enter="onSelect(item)"
        @keydown.space="(event: Event) => onSelect(item, event, true)"
        @longItemText="trackPointer"
      />
    </ul>
    <LayoutSearchState
      v-if="!items.length && !isLoading && mobileScreen"
      :empty="true"
      :searchString="searchString"
    />
    <slot />
    <TooltipStandard
      v-show="showTooltip"
      arrowPosition="top-center"
      :positionAbsoulute="true"
      class="tooltip"
      :pointerEvent="trackedMouseEvent"
      :text="hoveredTitle.toString()"
    />
  </div>
</template>

<script lang="ts" setup>
import { PropType, toRefs, ref, computed } from 'vue'
import ListRow from '@/design-system/atoms/ListRow.vue'
import TooltipStandard from '../atoms/TooltipStandard.vue'
import LayoutSearchState from './LayoutSearchState.vue'
import { useStore } from 'vuex'

const props = defineProps({
  title: {
    type: String,
    default: ''
  },
  icon: {
    type: Object,
    required: false,
    default: () => undefined
  },
  items: {
    type: Array as PropType<Array<any>>,
    default: () => []
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  hasTooltips: {
    type: Boolean,
    default: false
  },
  searchString: {
    type: String,
    default: ''
  }
})
const { items, isLoading, searchString } = toRefs(props)
interface EMITS {
  (e: 'select', item: object): void
}
const emits = defineEmits<EMITS>()
const showTooltip = ref(false)
const trackedMouseEvent = ref<object>()
const hoveredTitle = ref<string>('')
const store = useStore()
const mobileScreen = computed<boolean>(() => store.state.mobileScreen)

function onSelect(item: object, event?: Event, preventDefault?: boolean) {
  if (event && preventDefault) event.preventDefault() // Prevent space bar scrolling
  emits('select', item)
}

function trackPointer(event, header?: string, subheader?: string) {
  const rect = event.currentTarget.getBoundingClientRect()
  if (event) {
    trackedMouseEvent.value = {
      x: rect.x + rect.width / 2,
      y: rect.bottom - 45
    }
    const targetEl = event.target as HTMLElement
    targetEl.addEventListener('mouseleave', () => (showTooltip.value = false))

    if (header) {
      hoveredTitle.value = header
      if (subheader) hoveredTitle.value += '\n\n (' + subheader + ')'
    } else if (!header && subheader) hoveredTitle.value = subheader
    showTooltip.value = true
  }
}
</script>
<style lang="scss">
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/mixins.scss';
.list {
  display: flex;
  flex-direction: column;
  row-gap: variables.$gap-xlarge;
  &__header {
    display: flex;
    justify-content: space-between;
    padding-right: variables.$padding-large;
    @include font-mixins.desktop-bold-paragraph;
    &-title {
      column-gap: variables.$gap-large;
      display: flex;
    }
    &-w-icon + .list__items {
      margin: 0 variables.$margin-xlarge;
      padding-left: variables.$padding-xlarge;
    }
  }
  &__items {
    display: flex;
    flex-direction: column;
    margin: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding-left: 0;
    row-gap: variables.$gap-xlarge;
    @include mixins.scrollbar-vertical;
  }
  .tooltip {
    margin-top: -(variables.$margin-medium);
    width: max-content;
    z-index: 3;
  }
}
</style>
